"use client";

import { useResizeObserver } from "@faire/react/hooks/useResizeObserver";
import { mergeRefs } from "@faire/slate/lib/mergeRefs";
import { tsv } from "@faire/slate/lib/tsv";
import React, { useLayoutEffect, useRef } from "react";

import { useModalContext } from "./ModalContext";
import { useModalScrollContext } from "./ModalScrollContext";

type IProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
};

export const ModalFooterBase: React.FC<IProps> = ({
  children,
  className,
  ...props
}) => {
  const { modalWidth: width, setFooterHeight } = useModalContext();
  const { isAtBottom } = useModalScrollContext();
  const footerRef = useRef<HTMLDivElement>(null);
  const resizeRef = useResizeObserver<HTMLDivElement>((entry) => {
    setFooterHeight(entry.target.clientHeight);
  });

  const ref = mergeRefs(footerRef, resizeRef);

  useLayoutEffect(() => {
    setFooterHeight(footerRef.current?.clientHeight ?? 0);
  }, [setFooterHeight]);

  return (
    <div
      {...props}
      className={footerStyle.cls({
        width,
        className,
        hasShadow: !isAtBottom,
      })}
      ref={ref}
    >
      {children}
    </div>
  );
};

const footerStyle = tsv({
  base: "fs-modal__footer",
  variants: {
    width: {
      small: "",
      medium: "",
      large: "fs-modal__footer--large",
      xlarge: "fs-modal__footer--xlarge",
      custom: "",
    },
    hasShadow: {
      true: "border-fs-border-muted shadow-(--fs-modal-footer-shadow)",
    },
  },
});
